// The seam between the shell and the dock that renders it (slice 05).
//
// The shell decides WHAT sits where — tools in a bar, or nothing — and the
// dock (dockview, in `Dock.tsx`) renders it and serialises it. Everything the
// shell asks of the dock goes through this handle, so the shell's logic is
// tested against a fake and dockview is exercised only in a real browser.
//
// The rules the handle keeps: a side bar holds a LIST OF TABS, one showing
// (qa-runs#404); the bottom bar and the second window's centre hold one tool;
// a tool is in one bar at most; and a bar never vanishes when it empties —
// its group then holds the placeholder `empty:<slot>`, "nothing here yet",
// with a `+` on it (design/07 §1).
import {
  SLOTS, TOOL_PANEL_PREFIX, barsOfDock, isTabbedSlot,
  type BarTabs, type DeviceClass, type DockBars, type DockLayout, type Role, type SlotId, type ViewId,
} from '@dragons-table/vtt-shared';

export type ToolId = string;
/** The tool SHOWING in each bar (null when the bar is empty): what decides a bar's size and what the person sees. */
export type DockSlots = Record<SlotId, ToolId | null>;
export type { BarTabs, DockBars };
/** Where a tab's menu moves it: a place left or right in its bar, or to the other side bar. */
export type TabMove = 'left' | 'right' | 'other';

export const EMPTY_PREFIX = 'empty:';
export const TOOL_PREFIX = TOOL_PANEL_PREFIX;
/** The main window's centre is the board, which is not a tool and never moves. */
export const BOARD_PANEL = 'board';

export interface DockHandle {
  /**
   * Put a tool in a bar and show it. In a side bar already holding tools it is a NEW TAB beside them (qa-runs#404); in
   * an empty bar, the bottom bar or the second window's centre it takes the bar. The tool leaves any other bar it held.
   */
  place(slot: SlotId, tool: ToolId): void;
  /** Take a tool off its bar: its tab closes; the last tab leaves the empty bar with its `+`. */
  close(tool: ToolId): void;
  /** Empty a bar: every tab goes and the placeholder with its `+` takes their place. */
  clear(slot: SlotId): void;
  /** Bring a placed tool's tab to the front of its bar. */
  show(tool: ToolId): void;
  /** Move a tool's tab left or right in its bar, or to the other side bar (shown there). No dragging (design/07 §1). */
  move(tool: ToolId, where: TabMove): void;
  slots(): DockSlots;
  bars(): DockBars;
  toJSON(): DockLayout;
  /** Load a serialised layout, or the stock arrangement for `null`. */
  load(layout: DockLayout | null): void;
}

/** The slots a window offers: the second window's centre holds a tool where the main window's holds the board. */
export function slotsOf(view: ViewId): readonly SlotId[] {
  return view === 'main' ? ['left', 'right', 'bottom'] : SLOTS;
}

export const panelIdFor = (tool: ToolId): string => `${TOOL_PREFIX}${tool}`;
export const emptyIdFor = (slot: SlotId): string => `${EMPTY_PREFIX}${slot}`;

/** The tool a panel id names, or null for a placeholder or the board. */
export function toolOfPanel(panelId: string | undefined): ToolId | null {
  if (!panelId || !panelId.startsWith(TOOL_PREFIX)) return null;
  const tool = panelId.slice(TOOL_PREFIX.length);
  return tool.length > 0 ? tool : null;
}

/** One tool per bar, as the stock and the bottom bar have it, as bars. */
export function barsOfSlots(slots: DockSlots): DockBars {
  const out = {} as DockBars;
  for (const s of SLOTS) out[s] = { tabs: slots[s] ? [slots[s]!] : [], showing: slots[s] };
  return out;
}

/** The tool showing in each bar. */
export function showingOf(bars: DockBars): DockSlots {
  const out = {} as DockSlots;
  for (const s of SLOTS) out[s] = bars[s].showing;
  return out;
}

/** The bar a tool sits in, showing or not, or null. */
export function barOf(bars: DockBars, tool: ToolId): SlotId | null {
  return SLOTS.find((s) => bars[s].tabs.includes(tool)) ?? null;
}

/** The other side bar, for a tab's "move to the other bar"; null for a bar that is not a side bar. */
export const otherSide = (slot: SlotId): SlotId | null => (slot === 'left' ? 'right' : slot === 'right' ? 'left' : null);

/**
 * What each bar holds in a serialised layout (qa-runs#404): its tabs in order and the one showing. `null` is the stock;
 * a layout stored before tabs reads as one tab per bar (`barsOfDock`). Pure, so the menus can mark placements without
 * asking the dock.
 */
export function barsFromLayout(layout: DockLayout | null, stock: DockSlots): DockBars {
  return barsOfDock(layout) ?? barsOfSlots(stock);
}

/** The tool showing in each bar of a serialised layout. */
export function slotsFromLayout(layout: DockLayout | null, stock: DockSlots): DockSlots {
  return showingOf(barsFromLayout(layout, stock));
}

/**
 * The bars after placing a tool (the rule `place` keeps, for the fake dock and for reading ahead): a new tab in a used
 * side bar, the whole bar elsewhere; the tool leaves every other bar.
 */
export function placed(bars: DockBars, slot: SlotId, tool: ToolId): DockBars {
  const out = {} as DockBars;
  for (const s of SLOTS) out[s] = s === slot ? { ...bars[s] } : without(bars[s], tool);
  const bar = out[slot];
  if (bar.tabs.includes(tool)) out[slot] = { ...bar, showing: tool };
  else out[slot] = isTabbedSlot(slot) && bar.tabs.length > 0 ? { tabs: [...bar.tabs, tool], showing: tool } : { tabs: [tool], showing: tool };
  return out;
}

/** A bar without a tool: the tab beside it shows if it was showing (the next, else the one before). */
export function without(bar: BarTabs, tool: ToolId): BarTabs {
  const i = bar.tabs.indexOf(tool);
  if (i < 0) return { ...bar, tabs: [...bar.tabs] };
  const tabs = bar.tabs.filter((t) => t !== tool);
  return { tabs, showing: bar.showing === tool ? tabs[i] ?? tabs[i - 1] ?? null : bar.showing };
}

/** The bars after a tab's move, or the same bars when it cannot go that way (the end of its bar, or not a side bar). */
export function moved(bars: DockBars, tool: ToolId, where: TabMove): DockBars {
  const from = barOf(bars, tool);
  if (!from || !isTabbedSlot(from)) return bars;
  if (where === 'other') return placed(bars, otherSide(from)!, tool);
  const tabs = [...bars[from].tabs];
  const i = tabs.indexOf(tool);
  const j = where === 'left' ? i - 1 : i + 1;
  if (j < 0 || j >= tabs.length) return bars;
  [tabs[i], tabs[j]] = [tabs[j]!, tabs[i]!];
  return { ...bars, [from]: { ...bars[from], tabs } };
}

/**
 * The stock arrangement (design/07 §3): on a desktop identical for DM and player; the second window starts with the
 * dice bag in its centre. On a tablet (issue 1179) the stock is per role: the DM's is the desktop's, fitted to the
 * screen; a player's left bar starts empty — a player's Properties shows nothing until a token is chosen (design/07
 * §4), and the token's menu brings it to that free bar when asked — so a player's board has the room. One tool per bar:
 * a brand-new person's table has no second tab anywhere (qa-runs#404).
 */
export function stockSlots(view: ViewId, device: DeviceClass = 'desktop', role: Role | string = 'player'): DockSlots {
  if (view !== 'main') return { left: null, right: null, bottom: null, center: 'dice' };
  if (device === 'tablet' && role !== 'dm' && role !== 'codm') return { left: null, right: 'dice', bottom: 'initiative', center: null };
  return { left: 'properties', right: 'dice', bottom: 'initiative', center: null };
}

/** A shipped preset's bars (the Fight layout), or null for dockview's own form and the stock. */
export function barsOfDockSafe(layout: DockLayout | null): DockBars | null {
  return layout && typeof layout === 'object' && 'shipped' in layout ? barsOfDock(layout) : null;
}
